import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "@/redux";
import { MetaData } from "@/schemas/meta-data";

import { listApi } from "@/pages/Private/redux/list/list.api";

import { searchApi } from "./search.api";
import {
	GlobalSearchContactUpdate,
	IPersonSummary,
	ListPersonGlobalSearchInfoDTO,
	OrganizationGlobalSearchInfoDTO,
} from "./search.schema";

export interface SearchSelectedCompany {
	id: number;
	name: string;
	domain: string | null;
	logoUrl: string | null;
}

export interface SearchContactsBucket {
	data: ListPersonGlobalSearchInfoDTO[];
	meta: MetaData | null;
}

export interface SearchCompanyEnrichBucket {
	items: IPersonSummary[];
	page: number;
	totalPages: number;
	totalItems: number;
}

export interface SearchOrganizationsBucket {
	data: OrganizationGlobalSearchInfoDTO[];
	meta: MetaData | null;
}

export interface SearchState {
	contacts: SearchContactsBucket;
	companyEnrich: SearchCompanyEnrichBucket;
	// null when the search was scoped to a specific organization
	organizations: SearchOrganizationsBucket | null;
	selectedCompany: SearchSelectedCompany | null;
	isLoading: boolean;
	error: string | null;
}

const emptyContacts = (): SearchContactsBucket => ({ data: [], meta: null });
const emptyCompanyEnrich = (): SearchCompanyEnrichBucket => ({
	items: [],
	page: 1,
	totalPages: 0,
	totalItems: 0,
});
const emptyOrganizations = (): SearchOrganizationsBucket => ({ data: [], meta: null });

const initialState: SearchState = {
	contacts: emptyContacts(),
	companyEnrich: emptyCompanyEnrich(),
	organizations: emptyOrganizations(),
	selectedCompany: null,
	isLoading: false,
	error: null,
};

export const searchSlice = createSlice({
	name: "search",
	initialState,
	reducers: {
		setSelectedCompany: (state, { payload }: PayloadAction<SearchSelectedCompany>) => {
			state.selectedCompany = payload;
		},
		clearSelectedCompany: (state) => {
			state.selectedCompany = null;
		},
		clearGlobalSearch: (state) => {
			state.contacts = emptyContacts();
			state.companyEnrich = emptyCompanyEnrich();
			state.organizations = emptyOrganizations();
			state.selectedCompany = null;
			state.isLoading = false;
			state.error = null;
		},
		// Optimistic flip to "searching" the moment the user clicks the email/phone icon.
		setEnrichPersonSearching: (
			state,
			{ payload }: PayloadAction<{ profileInfoId: string; channel: "EMAIL" | "PHONE" }>
		) => {
			const item = state.companyEnrich.items.find(
				(person) => String(person.id) === payload.profileInfoId
			);

			if (!item) {
				return;
			}

			if (payload.channel === "EMAIL") {
				item.emailStatus = "SEARCHING";
			} else {
				item.phoneStatus = "SEARCHING";
			}
		},
		// The request behind `setEnrichPersonSearching` was refused (e.g. not enough credits, PREC-4437):
		// put back the status the click replaced, so the icon does not read "searching" for good.
		revertEnrichPersonSearching: (
			state,
			{
				payload,
			}: PayloadAction<{
				profileInfoId: string;
				channel: "EMAIL" | "PHONE";
				status: string | null | undefined;
			}>
		) => {
			const item = state.companyEnrich.items.find(
				(person) => String(person.id) === payload.profileInfoId
			);

			if (!item) {
				return;
			}

			if (payload.channel === "EMAIL") {
				item.emailStatus = payload.status ?? null;
			} else {
				item.phoneStatus = payload.status ?? null;
			}
		},
		// Applied from the `all-data` websocket event when a contact-bucket enrichment job
		// finishes. The modal's contact rows live here (not in the list/contact slices), so they
		// need their own handler to reflect the found email/phone — keyed by prospect id, mirroring
		// the list slice's `addEmailData`.
		applySearchContactEmailData: (
			state,
			{
				payload,
			}: PayloadAction<{
				prospectId: number;
				email?: string | null;
				emailStatus?: string | null;
				phone?: string | null;
				phoneStatus?: string | null;
			}>
		) => {
			const contact = state.contacts.data.find((item) => item.prospect?.id === payload.prospectId);

			if (!contact?.prospect) {
				return;
			}

			if (payload.emailStatus) {
				contact.prospect.email = payload.email ?? contact.prospect.email;
				contact.prospect.emailStatus = payload.emailStatus;
			} else if (payload.phoneStatus) {
				contact.prospect.phone = payload.phone ?? contact.prospect.phone;
				contact.prospect.phoneStatus = payload.phoneStatus;
			}
		},
		// Applied from the `global-search-contact-found` websocket event.
		updateEnrichPersonContact: (state, { payload }: PayloadAction<GlobalSearchContactUpdate>) => {
			const item = state.companyEnrich.items.find(
				(person) => String(person.id) === payload.profileInfoId
			);

			if (!item) {
				return;
			}

			if (payload.prospectId != null) {
				item.prospectId = payload.prospectId;
			}

			if (payload.channel === "EMAIL") {
				item.email = payload.email;
				item.emailStatus = payload.emailStatus;
			} else {
				item.phone = payload.phone;
				item.phoneStatus = payload.phoneStatus;
			}
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(searchApi.endpoints.globalSearch.matchPending, (state) => {
			state.isLoading = true;
			state.error = null;
		});
		builder.addMatcher(searchApi.endpoints.globalSearch.matchFulfilled, (state, { payload }) => {
			state.isLoading = false;
			state.error = null;
			state.contacts = {
				data: payload.contacts.data,
				meta: payload.contacts.meta ?? null,
			};
			state.companyEnrich = {
				items: payload.companyEnrich.items,
				page: payload.companyEnrich.page,
				totalPages: payload.companyEnrich.totalPages,
				totalItems: payload.companyEnrich.totalItems,
			};
			// backend returns null when scoped to a specific organization
			state.organizations = payload.organizations
				? { data: payload.organizations.data, meta: payload.organizations.meta ?? null }
				: null;
		});
		builder.addMatcher(searchApi.endpoints.globalSearch.matchRejected, (state, { error }) => {
			state.isLoading = false;
			state.error = error?.message ?? "Global search failed";
		});
		// Clicking "Find email"/"Find phone" on a contact-bucket row dispatches the list-domain
		// searchForEmails/searchForPhones mutation (via ContactChannels domain="list"). The response
		// carries the row's listPerson id + the new status (SEARCHING), so flip the matching contact
		// row to show the searching spinner — the list inbox does the same via its own matcher.
		builder.addMatcher(listApi.endpoints.searchForEmails.matchFulfilled, (state, { payload }) => {
			for (const person of payload) {
				const contact = state.contacts.data.find((item) => item.id === person.id);

				if (contact?.prospect) {
					contact.prospect.emailStatus = person.emailStatus;
				}
			}
		});
		builder.addMatcher(listApi.endpoints.searchForPhones.matchFulfilled, (state, { payload }) => {
			for (const person of payload) {
				const contact = state.contacts.data.find((item) => item.id === person.id);

				if (contact?.prospect) {
					contact.prospect.phoneStatus = person.phoneStatus;
				}
			}
		});
		// When PausedTag calls updateList (pause/resume), the result carries
		// campaignStatus: LOADING. Propagate it so isLoading flips to true in
		// PausedTag, which starts its own polling interval.
		builder.addMatcher(listApi.endpoints.updateList.matchFulfilled, (state, { payload }) => {
			state.contacts.data = state.contacts.data.map((contact) =>
				contact.list?.id === payload.id
					? ({ ...contact, list: payload } as ListPersonGlobalSearchInfoDTO)
					: contact
			);
		});
		// When PausedTag's polling calls getList, propagate the resolved
		// campaignStatus (RUNNING/PAUSED) so isLoading flips to false and
		// isUpdating is cleared by the sawLoadingRef logic.
		builder.addMatcher(listApi.endpoints.getList.matchFulfilled, (state, { payload }) => {
			state.contacts.data = state.contacts.data.map((contact) =>
				contact.list?.id === payload.id
					? ({ ...contact, list: payload } as ListPersonGlobalSearchInfoDTO)
					: contact
			);
		});
	},
});

export const {
	setSelectedCompany,
	clearSelectedCompany,
	clearGlobalSearch,
	setEnrichPersonSearching,
	revertEnrichPersonSearching,
	updateEnrichPersonContact,
	applySearchContactEmailData,
} = searchSlice.actions;
export const searchReducer = searchSlice.reducer;
export const searchSelector = (state: RootState) => state[searchSlice.name];
